<template>
    <div class="flex h-full w-full overflow-hidden">
        <div class="navbar-side" :class="sidebarCollapsed ? 'navbar-side--collapsed' : ''">
            <div class="flex items-center justify-between" :class="sidebarCollapsed ? 'px-1' : ''">
                <Logo v-if="!sidebarCollapsed" class="w-3/5" :dark-mode="true" />
                <button
                    class="rounded-lg p-2 text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                    @click="toggleSidebar"
                >
                    <Icon
                        :icon="['far', sidebarCollapsed ? 'chevron-right' : 'chevron-left']"
                        class="text-sm"
                    />
                </button>
            </div>

            <nav class="mt-8 flex flex-col gap-1">
                <template v-if="isThemeClient">
                    <router-link
                        class="nav-link"
                        active-class="nav-link--active"
                        :to="{ name: 'ClientCalendar' }"
                        :title="sidebarCollapsed ? $t('navigation.calendar') : ''"
                    >
                        <Icon :icon="['far', 'calendar']" class="nav-link-icon" />
                        <span v-if="!sidebarCollapsed" class="nav-link-text">
                            {{ $t('navigation.calendar') }}
                        </span>
                    </router-link>
                    <router-link
                        class="nav-link"
                        active-class="nav-link--active"
                        :to="{ name: 'ClientProfessionals' }"
                        :title="sidebarCollapsed ? $t('navigation.professionals') : ''"
                    >
                        <Icon :icon="['far', 'address-book']" class="nav-link-icon" />
                        <span v-if="!sidebarCollapsed" class="nav-link-text">
                            {{ $t('navigation.professionals') }}
                        </span>
                    </router-link>
                </template>
                <template v-else>
                    <div class="relative mb-4">
                        <button
                            class="btn w-full justify-center py-2.5"
                            :class="[
                                { highlighted: onboardingSlide === 'create-event-slide' },
                                sidebarCollapsed ? '!px-0' : '',
                            ]"
                            @click="showEventActions = !showEventActions"
                        >
                            <Icon :icon="['far', 'circle-plus']" />
                            <span v-if="!sidebarCollapsed" class="ml-2 text-sm">
                                {{ $t('navigation.create_event') }}
                            </span>
                        </button>
                        <transition enter-class="opacity-0" leave-to-class="opacity-0">
                            <NewEventPopup
                                v-if="showEventActions"
                                v-on-clickaway="hideEventActions"
                                @open-modal="openModal"
                            />
                        </transition>
                    </div>
                    <router-link
                        class="nav-link"
                        active-class="nav-link--active"
                        :to="{ name: 'ProfessionalCalendar' }"
                        :title="sidebarCollapsed ? $t('navigation.calendar') : ''"
                    >
                        <Icon :icon="['far', 'calendar']" class="nav-link-icon" />
                        <span v-if="!sidebarCollapsed" class="nav-link-text">
                            {{ $t('navigation.calendar') }}
                        </span>
                    </router-link>
                    <router-link
                        :class="[
                            'nav-link',
                            { highlighted: onboardingSlide === 'invite-clients-slide' },
                        ]"
                        active-class="nav-link--active"
                        :to="{ name: 'ProfessionalClients' }"
                        :title="sidebarCollapsed ? $t('navigation.client_book') : ''"
                    >
                        <Icon :icon="['far', 'address-book']" class="nav-link-icon" />
                        <span v-if="!sidebarCollapsed" class="nav-link-text">
                            {{ $t('navigation.client_book') }}
                        </span>
                    </router-link>
                </template>
            </nav>

            <!-- Theme Switch -->
            <div class="mt-auto border-t border-white/10 pt-4">
                <router-link
                    :to="{
                        name: isThemeClient ? 'ProfessionalCalendar' : 'ClientCalendar',
                    }"
                    class="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2.5 text-white/70 transition-all duration-200 hover:bg-white/10 hover:text-white"
                    :title="
                        isThemeClient
                            ? $t('navigation.switch_to_pro')
                            : $t('navigation.switch_to_client')
                    "
                >
                    <Icon :icon="['far', 'people-arrows']" class="people-icon w-5 flex-shrink-0" />
                    <span v-if="!sidebarCollapsed" class="truncate text-sm">
                        {{
                            isThemeClient
                                ? $t('navigation.switch_to_pro')
                                : $t('navigation.switch_to_client')
                        }}
                    </span>
                </router-link>
            </div>

            <!-- Profile at bottom -->
            <div
                class="cursor-pointer border-t border-white/10 pt-4"
                @click="showProfileActions = !showProfileActions"
            >
                <div class="flex items-center gap-3 rounded-lg p-2 hover:bg-white/10">
                    <Avatar :profile="profile" size="small" />
                    <div v-if="!sidebarCollapsed" class="min-w-0 flex-1 text-white">
                        <div
                            v-if="profile && profile.full_name"
                            class="truncate text-sm font-medium"
                            :title="profile.full_name"
                        >
                            {{ profile.full_name }}
                        </div>
                        <div class="truncate text-xs text-white/50">
                            <span v-if="isThemeClient">{{ $t('navigation.client') }}</span>
                            <span v-else>{{ $t('navigation.professional') }}</span>
                        </div>
                    </div>
                    <Icon
                        v-if="!sidebarCollapsed"
                        :icon="['far', 'ellipsis-vertical']"
                        class="text-white/40"
                    />
                </div>
                <transition enter-class="opacity-0" leave-to-class="opacity-0">
                    <div
                        v-if="showProfileActions"
                        v-on-clickaway="hideProfileActions"
                        class="absolute bottom-20 left-2 right-2 rounded-xl bg-white p-2 shadow-xl transition-all duration-200"
                    >
                        <router-link
                            :to="{
                                name: isThemeClient ? 'ClientProfile' : 'ProfessionalProfile',
                            }"
                            class="profile-menu-item"
                        >
                            <Icon :icon="['far', 'user']" class="w-5" />
                            <span>{{ $t('navigation.my_profile') }}</span>
                        </router-link>
                        <router-link
                            :to="{
                                name: isThemeClient ? 'ClientSettings' : 'ProfessionalSettings',
                            }"
                            class="profile-menu-item"
                        >
                            <Icon :icon="['far', 'gear']" class="w-5" />
                            <span>{{ $t('navigation.settings') }}</span>
                        </router-link>
                        <router-link
                            :to="{ name: 'ProfessionalVouchers' }"
                            class="profile-menu-item"
                        >
                            <Icon :icon="['far', 'ticket']" class="w-5" />
                            <span>{{ $t('navigation.vouchers') }}</span>
                        </router-link>
                        <div class="my-1 border-t border-neutral-lighter"></div>
                        <button
                            class="profile-menu-item w-full text-error hover:text-red-700"
                            @click="logout"
                        >
                            <Icon :icon="['far', 'arrow-right-from-bracket']" class="w-5" />
                            <span>{{ $t('common.auth.log_out') }}</span>
                        </button>
                    </div>
                </transition>
            </div>
        </div>
        <div class="main-view">
            <router-view>
                <router-view name="form" />
            </router-view>
        </div>
        <div class="navbar z-40">
            <template v-if="isThemeClient">
                <router-link
                    v-slot="{ isActive, href, navigate }"
                    :to="{ name: 'ClientCalendar' }"
                    custom
                >
                    <a
                        :href="href"
                        :class="['navbar-item', isActive && 'navbar-item--active']"
                        @click="navigate"
                    >
                        <Icon :icon="[isActive ? 'fas' : 'far', 'calendar']" />
                        <div class="text-xs">{{ $t('navigation.calendar') }}</div>
                    </a>
                </router-link>
                <router-link
                    v-slot="{ isActive, href, navigate }"
                    :to="{ name: 'ClientProfessionals' }"
                >
                    <a
                        :href="href"
                        :class="['navbar-item', isActive && 'navbar-item--active']"
                        @click="navigate"
                    >
                        <Icon :icon="[isActive ? 'fas' : 'far', 'address-book']" />
                        <div class="text-xs">{{ $t('navigation.professionals') }}</div>
                    </a>
                </router-link>
                <router-link
                    v-slot="{ isActive, href, navigate }"
                    :to="{ name: 'ClientProfile' }"
                    custom
                >
                    <a
                        :href="href"
                        :class="['navbar-item', isActive && 'navbar-item--active']"
                        @click="navigate"
                    >
                        <Icon :icon="[isActive ? 'fas' : 'far', 'user']" />
                        <div class="text-xs">{{ $t('navigation.my_profile') }}</div>
                    </a>
                </router-link>
            </template>
            <template v-else>
                <router-link
                    v-slot="{ isActive, href, navigate }"
                    :class="{ highlighted: onboardingSlide === 'create-event-slide' }"
                    :to="{ name: 'ProfessionalCalendar' }"
                >
                    <a
                        :href="href"
                        :class="['navbar-item', isActive && !isOnboarding && 'navbar-item--active']"
                        @click="navigate"
                    >
                        <Icon :icon="[isActive ? 'fas' : 'far', 'calendar']" />
                        <div class="text-xs">{{ $t('navigation.calendar') }}</div>
                    </a>
                </router-link>

                <div class="relative">
                    <button
                        class="btn"
                        data-cy="new-event-mobile"
                        @click="showEventActions = !showEventActions"
                    >
                        <Icon
                            :icon="['far', 'plus']"
                            class="transform transition-all duration-200"
                            :class="{
                                'rotate-45': showEventActions,
                            }"
                        />
                    </button>
                    <transition
                        enter-class="translate-y-1/2 opacity-0"
                        leave-to-class="translate-y-1/2 opacity-0"
                    >
                        <NewEventPopup
                            v-if="showEventActions"
                            v-on-clickaway="hideEventActions"
                            mobile
                            @open-modal="openModal"
                        />
                    </transition>
                </div>
                <router-link
                    v-slot="{ isActive, href, navigate }"
                    :class="{ highlighted: onboardingSlide === 'invite-clients-slide' }"
                    :to="{ name: 'ProfessionalClients' }"
                >
                    <a
                        :href="href"
                        :class="['navbar-item', isActive && !isOnboarding && 'navbar-item--active']"
                        @click="navigate"
                    >
                        <Icon :icon="[isActive ? 'fas' : 'far', 'address-book']" />
                        <div class="text-xs">{{ $t('navigation.client_book') }}</div>
                    </a>
                </router-link>
                <router-link
                    v-slot="{ isActive, href, navigate }"
                    :to="{ name: isThemeClient ? 'ClientProfile' : 'ProfessionalProfile' }"
                >
                    <a
                        :href="href"
                        :class="['navbar-item', isActive && !isOnboarding && 'navbar-item--active']"
                        @click="navigate"
                    >
                        <Icon :icon="[isActive ? 'fas' : 'far', 'user']" />
                        <div class="text-xs">{{ $t('navigation.my_profile') }}</div>
                    </a>
                </router-link>
            </template>
        </div>
    </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import Logo from '@/components/general/Logo';
import Avatar from '@/components/general/Avatar';
import NewEventPopup from '@/components/general/NewEventPopup';
import EventModal from '@/components/modals/EventModal';
import ClassModal from '@/components/modals/ClassModal';
import SlotModal from '@/components/modals/SlotModal';
import { directive as onClickaway } from 'vue-clickaway';
import { AUTH_SIGNOUT, CALENDAR_DRAFT_EVENT_SET } from '@/constants';
import FreeLabel from '@/components/general/FreeLabel';

const SIDEBAR_COLLAPSED_KEY = 'my-day.sidebar-collapsed';

@Component({
    components: {
        Logo,
        Avatar,
        NewEventPopup,
        FreeLabel,
    },
    directives: {
        onClickaway: onClickaway,
    },
})
export default class Navigation extends Vue {
    showEventActions = false;
    showProfileActions = false;
    draftModalProps = {};
    sidebarCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';

    modals = {
        EventModal,
        ClassModal,
        SlotModal,
    };

    mounted() {
        this.draftModalProps = {};
        this.$root.$on('new-event-popup-open', ({ draftModalProps }) => {
            if (this.isThemeClient) return;
            this.draftModalProps = draftModalProps;
            this.showEventActions = true;
        });
    }

    created() {
        // Close the popup when navigating back
        const unregisterRouterGuard = this.$router.beforeEach((_, __, next) => {
            this.hideEventActions();
            next();
        });

        this.$once('hook:destroyed', () => {
            unregisterRouterGuard();
        });
    }

    get isThemeClient() {
        return this.$store.getters.isThemeClient;
    }

    get isOnboarding() {
        return this.$store.state.onboarding.show;
    }

    get onboardingSlide() {
        return this.$store.state.onboarding.onboardingSlide;
    }

    get profile() {
        return this.$store.state.profile.user?.[this.$store.state.config.theme];
    }

    toggleSidebar() {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, this.sidebarCollapsed);
    }

    hideProfileActions() {
        if (this.showProfileActions) {
            this.showProfileActions = false;
        }
    }

    hideEventActions() {
        if (this.showEventActions) {
            this.$store.commit(CALENDAR_DRAFT_EVENT_SET, null);
            this.showEventActions = false;
        }
    }

    openModal(modal) {
        // Reset the draft event from the calendar if any.
        this.$store.commit(CALENDAR_DRAFT_EVENT_SET, null);
        this.$root.$emit('modal-open', {
            modal: this.modals[modal],
            props: this.draftModalProps,
        });
        this.draftModalProps = {};
    }

    async logout() {
        await this.$store.dispatch(AUTH_SIGNOUT);
        this.$router.push({
            name: this.isThemeClient ? 'ClientAuthLogIn' : 'ProfessionalAuthLogIn',
        });
    }
}
</script>

<style lang="postcss">
.highlighted {
    @apply relative z-50 text-primary outline-none;
}

.navbar-side {
    @apply relative hidden h-full flex-none flex-col bg-layer-dark p-4 text-sm font-normal;
    width: 220px;
    transition: width 0.2s ease;
}

.navbar-side--collapsed {
    width: 68px;
}

.nav-link {
    @apply flex items-center gap-3 rounded-lg px-3 py-2.5 text-white/70 outline-none transition-all duration-200 hover:bg-white/10 hover:text-white;
}

.nav-link--active {
    @apply bg-white/10 text-white;
}

.nav-link-icon {
    @apply w-5 flex-shrink-0 text-center text-base;
}

.nav-link-text {
    @apply truncate;
}

.profile-menu-item {
    @apply flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-darkest transition-colors duration-200 hover:bg-neutral-lightest;
}

.main-view {
    @apply h-full w-full overflow-y-auto overflow-x-hidden pb-20;
}

.navbar {
    @apply fixed bottom-0 flex w-full items-center justify-around border-t border-neutral-lighter bg-white text-lg font-normal;
    height: 72px;
    padding-bottom: calc(0.5 * env(safe-area-inset-bottom));
}

.navbar-item {
    @apply flex w-12 flex-col items-center justify-center rounded-xl pt-1 outline-none transition-all duration-200;
    min-height: 44px;
}

.navbar-item:hover {
    @apply bg-primary-lightest text-primary;
}

.navbar-item:focus,
.navbar-item:active {
    @apply text-primary outline-none;
}

.navbar-item--active {
    @apply font-medium text-primary;
}

@media (orientation: landscape) {
    .navbar {
        display: none;
    }

    .main-view {
        padding-bottom: 0;
    }
}

@media (min-width: 900px) {
    .navbar {
        display: none;
    }

    .navbar-side {
        display: flex;
    }

    .main-view {
        padding-bottom: 0;
    }
}

.svg-inline--fa.people-icon {
    width: 1em;
}
</style>
